import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {ArrowRight, Check, X} from 'lucide-react';
import {actionApi, ActionSubject, Proposal} from './actionApi';
import {copilotError} from './copilotApi';

const Card = styled.section`border: 1px solid #334155; border-radius: 10px; padding: 12px 14px; margin: 10px 0; font-size: 13px; line-height: 1.55; background: #0f1c2e;
  h4 {font-size: 13px; font-weight: 600; margin: 0 0 4px; color: #e2e8f0;} p {margin: 4px 0; color: #b7c5d8;} small {color: #94a3b8;}`;
const Severity = styled.span<{$critical: boolean}>`font-size: 10px; letter-spacing: .05em; color: ${p => p.$critical ? '#f4b6a7' : '#dfc48f'}; margin-left: 8px;`;
const Button = styled.button<{$primary?: boolean}>`display: inline-flex; align-items: center; gap: 5px; font: inherit; font-size: 12px; padding: 6px 10px; margin: 6px 8px 0 0; cursor: pointer; border-radius: 7px;
  border: 1px solid ${p => p.$primary ? '#93d8ed' : '#3a4c62'}; background: ${p => p.$primary ? '#a4dfef' : '#152438'}; color: ${p => p.$primary ? '#102234' : '#d2e4f3'};
  &:disabled {opacity: .45; cursor: default;}`;

/**
 * Proactive work (FRD v2 §9.3), shown when the rail opens on an ABF facility or a private credit facility
 * (slice 6): each unread material change with its proposed next step and owner. Approve assigns through the
 * work queue; Skip hides it for you only. Critical or blocking items can only be opened.
 */
export function ProposalOpener({subject, canTriage}: {subject: ActionSubject; canTriage: boolean}) {
  const navigate = useNavigate();
  const [items, setItems] = useState<Proposal[]>([]);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const key = subject.loanId != null ? `${subject.loanSource}:${subject.loanId}` : String(subject.facilityId);
  // The subject is read through its key, so a re-rendered object with the same ids does not reload.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback((signal?: AbortSignal) => actionApi.proposals(subject, signal).then(setItems).catch(() => setItems([])), [key]);
  useEffect(() => {const controller = new AbortController(); void load(controller.signal); return () => controller.abort();}, [load]);
  if(!items.length && !done) return null;
  const act = async (item: Proposal, kind: 'approve' | 'skip') => {
    setBusy(item.id); setError('');
    try {
      await (kind === 'approve' ? actionApi.approveProposal(item.id, subject) : actionApi.skipProposal(item.id, subject));
      setDone(kind === 'approve' ? `Assigned to ${item.proposal.suggestedOwner}.` : 'Hidden for you. Nothing else changed.');
      await load();
    } catch(e) {setError(copilotError(e));} finally {setBusy(undefined);}
  };
  return <div aria-label="Proposed work">
    <small style={{color: '#9fb2cc'}}>{items.length ? `${items.length} change${items.length === 1 ? '' : 's'} since you last looked` : ''}</small>
    {items.slice(0, 3).map(item => <Card key={item.id}>
      <h4>{item.title}<Severity $critical={item.severity === 'CRITICAL'}>{item.severity}</Severity></h4>
      {item.proposal.rationale && item.proposal.rationale !== item.title && <p>{item.proposal.rationale}</p>}
      <p><strong style={{fontWeight: 500, color: '#e2e8f0'}}>Proposed:</strong> {item.proposal.action}
        {item.proposal.suggestedOwner && <> · {item.proposal.ownerSource === 'CURRENT_OWNER' ? 'owned by' : 'assign to'} {item.proposal.suggestedOwner}</>}
        {item.proposal.dueDate && <> · due {item.proposal.dueDate}</>}</p>
      {canTriage && item.proposal.canApprove && <Button $primary disabled={!!busy} onClick={() => act(item, 'approve')}><Check size={13} /> Approve</Button>}
      {canTriage && item.proposal.canSkip && <Button disabled={!!busy} onClick={() => act(item, 'skip')}><X size={13} /> Skip</Button>}
      <Button disabled={!!busy} onClick={() => navigate(item.route)}>Open <ArrowRight size={13} /></Button>
      {!item.proposal.canSkip && <p><small>{item.proposal.skipReason}</small></p>}
    </Card>)}
    {done && <p role="status" style={{fontSize: 12, color: '#97d8ba'}}>{done}</p>}
    {error && <p role="alert" style={{fontSize: 12, color: '#fcd34d'}}>{error}</p>}
  </div>;
}
